import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Check, XCircle, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { clearStoredCart } from '@/lib/cart';
import { trackPurchase } from '@/lib/analytics';
import { Button } from '@/components/ui/button';

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, status: null, error: null, purpose: null, amount: 0 });
  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) { setState({ loading: false, status: 'error', error: 'Missing session' }); return; }
    api.get(`/stripe/session/${sid}`)
      .then(r => {
        if (r.data.status === 'paid' && (r.data.purpose === 'cart' || r.data.purpose === 'product')) {
          clearStoredCart(); // shopping done — empty the local cart
          // Purchase fires ONLY on backend-verified paid Stripe sessions,
          // with the real order value/currency + catalog ids (deduped inside).
          trackPurchase(
            (r.data.amount_total || 0) / 100,
            r.data.order_ref,
            r.data.currency || 'USD',
            r.data.content_ids || [],
            r.data.num_items || 0,
          );
        }
        setState({ loading: false, status: r.data.status, purpose: r.data.purpose, amount: r.data.amount_total || 0, orderRef: r.data.order_ref || null });
      })
      .catch(e => setState({ loading: false, status: 'error', error: e.response?.data?.detail || 'Verification failed' }));
  }, [params]);
  return (
    <Layout>
      <section className="max-w-lg mx-auto px-4 sm:px-6 py-16 text-center">
        {state.loading ? (
          <>
            <Loader2 className="h-12 w-12 text-[#17352D] animate-spin mx-auto" />
            <h1 className="mt-4 font-headline text-3xl text-[#17352D]">Confirming payment...</h1>
          </>
        ) : state.status === 'paid' ? (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center"><Check className="h-8 w-8 text-emerald-700" /></div>
            <h1 className="mt-4 font-headline text-3xl text-[#17352D]">Payment received</h1>
            <p className="mt-2 text-slate-600">${(state.amount / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })} charged successfully. A receipt has been emailed to you.</p>
            {state.orderRef && (
              <div className="mt-4 rounded-2xl border border-[#E6E9EE] bg-[#F7F9F8] p-4" data-testid="success-order-ref">
                <div className="text-xs text-slate-500">Your order number</div>
                <div className="font-headline text-2xl text-[#17352D] tracking-wide">#{state.orderRef}</div>
                <Link to={`/track?q=${state.orderRef}`} className="mt-1 inline-block text-sm text-[#C8922F] hover:underline" data-testid="success-track-link">
                  Track your delivery →
                </Link>
              </div>
            )}
            <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
              {state.purpose === 'cart' || state.purpose === 'product' ? (
                <Button onClick={() => nav('/shop')} className="rounded-xl bg-[#17352D] text-white">Keep shopping</Button>
              ) : (
                <Button onClick={() => nav('/portal')} className="rounded-xl bg-[#17352D] text-white">Go to my portal</Button>
              )}
              <Button onClick={() => nav('/')} variant="outline" className="rounded-xl">Home</Button>
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center"><XCircle className="h-8 w-8 text-amber-700" /></div>
            <h1 className="mt-4 font-headline text-3xl text-[#17352D]">Payment status: {state.status || 'unknown'}</h1>
            <p className="mt-2 text-slate-600">{state.error || 'Your payment was not completed. You have not been charged.'}</p>
            <div className="mt-6"><Button onClick={() => nav('/')} className="rounded-xl bg-[#17352D] text-white">Home</Button></div>
          </>
        )}
      </section>
    </Layout>
  );
}

export function PaymentCancelledPage() {
  const nav = useNavigate();
  return (
    <Layout>
      <section className="max-w-lg mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center"><XCircle className="h-8 w-8 text-slate-500" /></div>
        <h1 className="mt-4 font-headline text-3xl text-[#17352D]">Payment cancelled</h1>
        <p className="mt-2 text-slate-600">No charge was made. You can retry anytime.</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
          <Button onClick={() => nav(-1)} className="rounded-xl bg-[#17352D] text-white">Go back</Button>
          <Button onClick={() => nav('/')} variant="outline" className="rounded-xl">Home</Button>
        </div>
      </section>
    </Layout>
  );
}
